import React from 'react';
import { motion } from 'framer-motion';
import { Coffee, Code, Heart, Envelope, MapPin, ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const AboutUsPage = () => {
  const { t } = useTranslation();

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {t('aboutUsPage.hero.greeting')} <span className="text-blue-300">{t('aboutUsPage.hero.name')}</span>
              </h1>
              <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
                {t('aboutUsPage.hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2"
                >
                  <MapPin className="w-5 h-5 text-blue-300" weight="thin" />
                  <span>{t('aboutUsPage.hero.location')}</span>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-2"
                >
                  <Coffee className="w-5 h-5 text-blue-300" weight="thin" />
                  <span>{t('aboutUsPage.hero.coffeeDriven')}</span>
                </motion.div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Foto van Julius */}
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/20">
                <img 
                  src={require('../assets/images/about/foto.png')} 
                  alt={t('aboutUsPage.altTexts.founderPhoto')}
                  className="w-full h-96 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personal Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {t('aboutUsPage.story.title')}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('aboutUsPage.story.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('aboutUsPage.story.developerToEntrepreneur.title')}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('aboutUsPage.story.developerToEntrepreneur.description1')}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {t('aboutUsPage.story.developerToEntrepreneur.description2')}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Werkfoto aan bureau */}
              <div className="bg-gray-100 rounded-2xl overflow-hidden">
                <img 
                  src={require('../assets/images/about/foto_bureau_1.jpg')} 
                  alt={t('aboutUsPage.altTexts.workPhoto')}
                  className="w-full h-80 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How I Work Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {t('aboutUsPage.howIWork.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('aboutUsPage.howIWork.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Coffee,
                title: t('aboutUsPage.howIWork.personalContact.title'),
                description: t('aboutUsPage.howIWork.personalContact.description'),
                color: "from-amber-500 to-orange-500"
              },
              {
                icon: Code,
                title: t('aboutUsPage.howIWork.transparentWork.title'),
                description: t('aboutUsPage.howIWork.transparentWork.description'),
                color: "from-blue-500 to-indigo-500"
              },
              {
                icon: Heart,
                title: t('aboutUsPage.howIWork.afterDelivery.title'),
                description: t('aboutUsPage.howIWork.afterDelivery.description'),
                color: "from-pink-500 to-red-500"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                  <item.icon className="w-8 h-8 text-white" weight="thin" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Foto van werkplek/setup */}
              <div className="bg-gray-100 rounded-2xl overflow-hidden">
                <img 
                  src={require('../assets/images/about/foto_setup.jpg')} 
                  alt={t('aboutUsPage.altTexts.setupPhoto')}
                  className="w-full h-64 object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-gray-100 rounded-2xl overflow-hidden">
                <img 
                  src={require('../assets/images/about/foto_setup_2.jpg')} 
                  alt={t('aboutUsPage.altTexts.brainstormPhoto')}
                  className="w-full h-64 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Simple Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('aboutUsPage.stats.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { number: "50+", label: t('aboutUsPage.stats.satisfiedCustomers') },
              { number: "2019", label: t('aboutUsPage.stats.foundedYear') },
              { number: "24/7", label: t('aboutUsPage.stats.supportAvailable') }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-6">
              {t('aboutUsPage.cta.title')}
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              {t('aboutUsPage.cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact"
                  className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Envelope className="w-5 h-5" weight="thin" />
                  {t('aboutUsPage.cta.sendMessage')}
                </Link>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/start-your-project"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {t('aboutUsPage.cta.startProject')}
                  <ArrowRight className="w-5 h-5" weight="thin" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutUsPage;
